import type { StorageLike } from './clock.ts';

export const FARM_QUEUE_KEY = 'threadbound.farm-queue.v1';

export interface FarmQueue {
  /** Next sequence the server has not accepted. */
  seq: number;
  stageId: number;
  count: number;
  startedAtMs: number;
}

export function loadFarmQueue(storage: StorageLike): FarmQueue | null {
  const raw = storage.getItem(FARM_QUEUE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as FarmQueue;
    if (!Number.isInteger(parsed.seq) || parsed.seq < 1) return null;
    if (!Number.isInteger(parsed.stageId) || parsed.stageId < 1) return null;
    if (!Number.isInteger(parsed.count) || parsed.count < 0) return null;
    if (!Number.isFinite(parsed.startedAtMs)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveFarmQueue(storage: StorageLike, queue: FarmQueue | null): void {
  if (!queue || queue.count < 1) {
    storage.removeItem(FARM_QUEUE_KEY);
    return;
  }
  storage.setItem(FARM_QUEUE_KEY, JSON.stringify(queue));
}

/**
 * After a settle, keep wins the server did not accept.
 * A replay stores the smaller count it already paid. The extra wins become the next sequence
 * instead of being dropped. A queue for a different sequence is left alone.
 */
export function carryFarmQueue(
  pending: FarmQueue | null,
  sent: { seq: number; stageId: number; count: number },
  accepted: { seq: number; requested: number } | null,
): FarmQueue | null {
  const credited =
    accepted && accepted.seq === sent.seq && Number.isInteger(accepted.requested) && accepted.requested >= 0
      ? accepted.requested
      : sent.count;
  if (pending && pending.seq === sent.seq && pending.stageId === sent.stageId && pending.count > credited) {
    return {
      seq: sent.seq + 1,
      stageId: pending.stageId,
      count: pending.count - credited,
      startedAtMs: Date.now(),
    };
  }
  if (!pending || pending.seq === sent.seq) return null;
  return pending;
}

/** Add one local win. A stage change replaces the queue; the caller flushes the old one first. */
export function enqueueFarmClear(queue: FarmQueue | null, stageId: number, seq: number, nowMs: number): FarmQueue {
  if (!queue || queue.count < 1 || queue.stageId !== stageId) {
    return { seq, stageId, count: 1, startedAtMs: nowMs };
  }
  return { ...queue, count: queue.count + 1 };
}
